import { Card, Group, Stack, Text, Badge } from '@mantine/core'
import type { FC, ReactNode } from 'react'
import { DatasetType, type AudienceConfiguration } from '@/lib/domain'
import { formatCompactNumber } from '@/lib/formatNumber'
import { cpmFor, formatCpm } from '@/lib/pricing'
import type { SegmentStage } from '@/features/segment/components/SegmentDetail'
import { segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import { SegmentStageFooter } from './SegmentStageFooter'
import { datasetLabel } from './renderMappers'
import { colorForDataset } from '@/lib/datasetColors'
import classes from './SegmentCartCard.module.css'

export interface SegmentCartCardProps {
    config: AudienceConfiguration
    threadId: string
    // Dataset id → display name, for the dataset chips under the title.
    names: Record<string, string>
    onDelete: () => void | Promise<unknown>
    onOpenDetail: (config: AudienceConfiguration, stage: SegmentStage) => void
    isDeleting?: boolean
    // The type-specific definition body (behaviors / attributes).
    children?: ReactNode
}

// The dataset(s) a config draws on: a crossed segment spans two (its transaction
// `dataset_id` and its user-attribute `users_with_attributes_dataset_id`); every
// other type draws on a single dataset.
const datasetIdsFor = (config: AudienceConfiguration): string[] =>
    config.dataset_type === DatasetType.CROSSED
        ? [config.dataset_id, config.users_with_attributes_dataset_id]
        : [config.dataset_id]

// Uniform chrome for every cart item, regardless of audience type: the title,
// the dataset(s) as chips beneath it, an optional description, the type-specific
// definition body, the reach, and the shared lifecycle footer. Deliberately no
// audience-type chip and no eye icon — navigation lives in the footer.
export const SegmentCartCard: FC<SegmentCartCardProps> = ({
    config,
    threadId,
    names,
    onDelete,
    onOpenDetail,
    isDeleting = false,
    children,
}) => {
    const title = config.name?.trim() || config.segment_id.replaceAll('_', ' ')
    const datasets = datasetIdsFor(config)
        .map((id) => ({ id, label: datasetLabel(id, names) }))
        .filter((d) => d.label)

    // CPM price (bottom-left, above the footer). Fixed by audience type, +10%
    // once the segment is modeled (a finalized model; a draft doesn't count yet).
    const { lifecycleFor } = useSegmentStubs()
    const life = lifecycleFor(segmentKey(config), threadId)
    const cpm = cpmFor(config.dataset_type, life.modelStatus === 'ready')

    return (
        <Card
            withBorder
            shadow='sm'
            radius='md'
            padding={0}
            style={{ flexShrink: 0 }}
        >
            <Stack
                gap='xs'
                p='lg'
                className={classes.body}
                style={{ minWidth: 0 }}
            >
                <div className={classes.header}>
                    <Text
                        fw={500}
                        size='md'
                        tt={config.name ? undefined : 'capitalize'}
                        className={classes.title}
                    >
                        {title}
                    </Text>

                    {config.audience_size > 0 && (
                        <div className={classes.reach}>
                            <Text
                                size='md'
                                fw={700}
                                lh={1.1}
                                c='var(--mantine-primary-color-filled)'
                            >
                                ~{formatCompactNumber(config.audience_size)}
                            </Text>
                            <Text
                                size='xs'
                                c='dimmed'
                                tt='uppercase'
                                fw={600}
                                lh={1.2}
                            >
                                Reach
                            </Text>
                        </div>
                    )}
                </div>

                {datasets.length > 0 && (
                    <Group gap={6}>
                        {datasets.map((d) => (
                            <Badge
                                key={d.id}
                                variant='light'
                                color={colorForDataset(d.id)}
                                size='sm'
                            >
                                {d.label}
                            </Badge>
                        ))}
                    </Group>
                )}

                {config.description && (
                    <Text size='sm' c='dimmed' lineClamp={2}>
                        {config.description}
                    </Text>
                )}

                {children}

                {cpm !== undefined && (
                    <Text size='sm' fw={600}>
                        {formatCpm(cpm)}{' '}
                        <Text span size='xs' c='dimmed' fw={500}>
                            CPM
                        </Text>
                    </Text>
                )}
            </Stack>
            <SegmentStageFooter
                config={config}
                threadId={threadId}
                onOpenDetail={onOpenDetail}
                onDelete={onDelete}
                isDeleting={isDeleting}
            />
        </Card>
    )
}
